import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconCheck, IconX } from '@tabler/icons-react';
import { SystemDataRefreshStatus, SystemDataRefreshUpdate } from '@lib/signalr/signalr.types';
import { systemKeys } from '@api/systems/systemQueries';
import { customerKeys } from '@api/customers/customerQueries';
import { formatDateTime } from '@utils/dateFormatters';

export function useRefreshNotifications(
  currentSystemId: string | null,
  unregisterActiveRefresh: (systemId: string) => void
) {
  const queryClient = useQueryClient();

  const handleRefreshUpdate = useCallback((update: SystemDataRefreshUpdate) => {
    console.debug('[SignalR] System data refresh update received:', update);

    const systemId = currentSystemId;
    if (!systemId) {
      console.warn('[SignalR] Received refresh update but no current system ID');
      return;
    }

    const notificationId = `system-refresh-${systemId}`;

    // Normalize status - SignalR sends string values that match enum values
    const status = update.status as string;
    console.log(status)

    if (status === SystemDataRefreshStatus.CommandSent) {
      // Show loading notification
      notifications.show({
        id: notificationId,
        title: 'Refreshing System Data',
        message: 'Collecting latest data from system agent...',
        color: 'blue',
        loading: true,
        autoClose: false,
      });
    } else if (status === SystemDataRefreshStatus.RequestReceived) {
      // Agent acknowledged - show in progress
      notifications.update({
        id: notificationId,
        title: 'Refreshing System Data',
        message: 'Agent is collecting data...',
        color: 'blue',
        loading: true,
        autoClose: false,
      });
    } else if (status === SystemDataRefreshStatus.Completed) {
      // Unregister active refresh - saga is complete
      unregisterActiveRefresh(systemId);

      // Check for partial success
      const allSuccess = update.environmentSuccess && update.applicationsSuccess && update.licensingSuccess;
      const anySuccess = update.environmentSuccess || update.applicationsSuccess || update.licensingSuccess;

      if (allSuccess) {
        // Full success
        const lastSyncMessage = update.dataRefreshedAt
          ? `System data successfully checked for updates! Last synced: ${formatDateTime(update.dataRefreshedAt)}`
          : 'All system data successfully checked for updates';

        notifications.update({
          id: notificationId,
          title: 'System Data Refreshed',
          message: lastSyncMessage,
          color: 'green',
          icon: <IconCheck size={16}/>,
          loading: false,
          autoClose: 8000,
        });
      } else if (anySuccess) {
        // Partial success - build message showing which succeeded
        const successParts: string[] = [];
        if (update.environmentSuccess) successParts.push('environment');
        if (update.applicationsSuccess) successParts.push('applications');
        if (update.licensingSuccess) successParts.push('licensing');

        notifications.update({
          id: notificationId,
          title: 'System Data Partially Refreshed',
          message: `Updated: ${successParts.join(', ')}`,
          color: 'yellow',
          icon: <IconAlertTriangle size={16}/>,
          loading: false,
          autoClose: 8000,
        });
      } else {
        // No data collected
        notifications.update({
          id: notificationId,
          title: 'System Refresh Failed',
          message: 'No data could be collected from the system',
          color: 'red',
          icon: <IconX size={16}/>,
          loading: false,
          autoClose: 8000,
        });
      }

      // Invalidate system detail queries to refresh UI with new data
      queryClient.invalidateQueries({
        queryKey: systemKeys.detail(systemId),
        exact: false,
        refetchType: 'active'  // Force refetch even if data is still "fresh" (within staleTime)
      });

      // Invalidate all customer detail queries (including customer systems lists)
      // This ensures "Last Sync" column updates when a system is refreshed
      queryClient.invalidateQueries({
        queryKey: customerKeys.details(),
        exact: false,
        refetchType: 'active'
      });
    } else if (status === SystemDataRefreshStatus.Failed || status === SystemDataRefreshStatus.TimedOut) {
      // Unregister active refresh - saga failed or timed out
      unregisterActiveRefresh(systemId);
      // Failed or timed out
      const title = status === SystemDataRefreshStatus.TimedOut
        ? 'System Refresh Timed Out'
        : 'System Refresh Failed';
      const message = status === SystemDataRefreshStatus.TimedOut
        ? 'The system did not respond in time'
        : 'An error occurred while refreshing system data';

      notifications.update({
        id: notificationId,
        title,
        message,
        color: 'red',
        icon: <IconX size={16}/>,
        loading: false,
        autoClose: 8000,
      });
    }
  }, [currentSystemId, queryClient, unregisterActiveRefresh]);

  return {handleRefreshUpdate};
}
